import React from 'react';

import { sitesApi, type BuilderCanonicalRenderSnapshot } from '../api';
import { availableWorkspaceWidth, CANVAS_ZOOM_STEPS, resolvedCanvasScale } from '../canvasZoom';
import type { CanvasZoom, DesktopViewportWidth } from '../canvasZoom';
import {
  DRAG_PLACEMENT_FIELDS,
  EDITOR_PROTOCOL_VERSION,
  RESIZE_PLACEMENT_FIELDS,
} from '../editorProtocolContract';
import type { GridPlacement, SiteChrome, SitePage, ViewportKey } from '../types';
import { parseCanvasMessage, validatePlacement, type EditorSelection } from './canonicalEditorProtocol';

const DESIGN_WIDTH: Record<ViewportKey, number> = {
  desktop: 1280,
  tablet: 834,
  mobile: 390,
};

function randomToken(): string {
  const bytes = new Uint32Array(4);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (value) => value.toString(16).padStart(8, '0')).join('');
}

/**
 * One iframe lifecycle. Every (re)load gets a fresh nonce and session so a
 * message from a previous, torn-down iframe can never be mistaken for the
 * current one.
 */
interface Lifecycle {
  key: number;
  nonce: string;
  session: string;
}

function newLifecycle(key: number): Lifecycle {
  if (typeof window === 'undefined') return { key, nonce: 'server', session: 'server' };
  return { key, nonce: randomToken(), session: randomToken() };
}

export type CanonicalRuntimeState = 'loading' | 'ready' | 'unavailable';

const HANDSHAKE_TIMEOUT_MS = 4000;
const MAX_HANDSHAKE_RETRIES = 3;

export interface CanonicalPlacementEdit {
  blockId: string;
  viewport: ViewportKey;
  placement: Partial<GridPlacement>;
}

interface CanonicalCanvasFrameProps {
  websiteId: string;
  editorCanvasUrl: string;
  revision: number;
  pages: SitePage[];
  site: SiteChrome;
  pageId: string;
  viewport: ViewportKey;
  desktopViewportWidth?: DesktopViewportWidth;
  selection?: EditorSelection;
  onSelectBlock: (blockId: string) => void;
  onSelectSection: (sectionId: string) => void;
  onSelectChrome: (chrome: 'header' | 'footer') => void;
  onNavigatePage: (pageId: string) => void;
  onEditText?: (blockId: string, text: string) => void;
  onMoveBlock?: (blockId: string, target: { sectionId: string; columnId: string; index: number }) => void;
  onSetPlacement?: (edit: CanonicalPlacementEdit) => void;
}

const NO_SELECTION: EditorSelection = { kind: 'none' };

function blocksOf(page: SitePage | undefined) {
  return page?.sections?.flatMap((section) => section.columns.flatMap((column) => column.blocks)) ?? page?.blocks ?? [];
}

function selectionExists(page: SitePage | undefined, selection: EditorSelection): boolean {
  if (selection.kind === 'block') return blocksOf(page).some((block) => block.blockId === selection.id);
  if (selection.kind === 'section') return Boolean(page?.sections?.some((section) => section.sectionId === selection.id));
  return true;
}

function parseOrigin(url: string): string | null {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

export function CanonicalCanvasFrame({
  websiteId,
  editorCanvasUrl,
  revision,
  pages,
  site,
  pageId,
  viewport,
  desktopViewportWidth = 1280,
  selection = NO_SELECTION,
  onSelectBlock,
  onSelectSection,
  onSelectChrome,
  onNavigatePage,
  onEditText,
  onMoveBlock,
  onSetPlacement,
}: CanonicalCanvasFrameProps) {
  const surfaceRef = React.useRef<HTMLDivElement | null>(null);
  const iframeRef = React.useRef<HTMLIFrameElement | null>(null);
  const [lifecycle, setLifecycle] = React.useState<Lifecycle>(() => newLifecycle(0));
  const lifecycleRef = React.useRef(lifecycle);
  lifecycleRef.current = lifecycle;
  const renderIdRef = React.useRef(0);
  const requestSequenceRef = React.useRef(0);
  const [surfaceWidth, setSurfaceWidth] = React.useState(0);
  const [canvasHeight, setCanvasHeight] = React.useState(900);
  const canvasHeightRef = React.useRef(900);
  const [readyGeneration, setReadyGeneration] = React.useState(0);
  const [runtimeState, setRuntimeState] = React.useState<CanonicalRuntimeState>('loading');
  const [snapshot, setSnapshot] = React.useState<BuilderCanonicalRenderSnapshot | null>(null);
  const [renderError, setRenderError] = React.useState<string | null>(null);
  const [runtimeFailure, setRuntimeFailure] = React.useState<string | null>(null);
  const timeoutRef = React.useRef<number | null>(null);
  const attemptRef = React.useRef(0);
  const readyRef = React.useRef(false);

  const canvasOrigin = React.useMemo(() => parseOrigin(editorCanvasUrl), [editorCanvasUrl]);
  const iframeSrc = React.useMemo(
    () => `${editorCanvasUrl}#nonce=${lifecycle.nonce}&session=${lifecycle.session}`,
    [editorCanvasUrl, lifecycle.nonce, lifecycle.session],
  );
  const logicalWidth = viewport === 'desktop' ? desktopViewportWidth : DESIGN_WIDTH[viewport];

  // Zoom is host/editor presentation state only: it controls how large the
  // logical canvas is painted, never the canonical render-contract payload
  // (viewport.width stays the authored logical width). Fit is the safe
  // default and never upscales past 100%; a manual value above Fit is
  // allowed to overflow into horizontal scroll rather than shrink the site.
  const [zoom, setZoom] = React.useState<CanvasZoom>('fit');
  const scale = resolvedCanvasScale(zoom, surfaceWidth, logicalWidth);

  React.useLayoutEffect(() => {
    const surface = surfaceRef.current;
    if (!surface) return;
    const measure = () => {
      const styles = getComputedStyle(surface);
      const width = availableWorkspaceWidth(
        surface.clientWidth,
        Number.parseFloat(styles.paddingLeft || '0'),
        Number.parseFloat(styles.paddingRight || '0'),
      );
      setSurfaceWidth((current) => (current === width ? current : width));
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(surface);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  React.useEffect(() => {
    const sequence = ++requestSequenceRef.current;
    const timer = window.setTimeout(() => {
      void sitesApi
        .renderDraft(websiteId, revision, pages, site)
        .then((next) => {
          if (requestSequenceRef.current !== sequence) return;
          setSnapshot(next);
          setRenderError(null);
        })
        .catch((error: unknown) => {
          if (requestSequenceRef.current !== sequence) return;
          setRenderError(error instanceof Error ? error.message : 'Canonical render-projectie mislukt.');
        });
    }, 100);
    return () => window.clearTimeout(timer);
  }, [pages, revision, site, websiteId]);

  // Platform state is the only authority: every inbound proposal is checked
  // against the *current* props through this ref, never against iframe claims.
  const snapshotRef = React.useRef<BuilderCanonicalRenderSnapshot | null>(null);
  React.useEffect(() => {
    snapshotRef.current = snapshot;
  }, [snapshot]);
  const authorityRef = React.useRef({ pages, pageId, viewport, selection });
  authorityRef.current = { pages, pageId, viewport, selection };

  const handlersRef = React.useRef({ onSelectBlock, onSelectSection, onSelectChrome, onNavigatePage, onEditText, onMoveBlock, onSetPlacement });
  React.useEffect(() => {
    handlersRef.current = { onSelectBlock, onSelectSection, onSelectChrome, onNavigatePage, onEditText, onMoveBlock, onSetPlacement };
  }, [onEditText, onMoveBlock, onNavigatePage, onSelectBlock, onSelectChrome, onSelectSection, onSetPlacement]);

  const clearTimer = React.useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Bounded retry: reload the iframe with a brand-new lifecycle; once the
  // budget is spent, stop and surface a deterministic unavailable state.
  // Never falls back to legacy rendering.
  const failLifecycle = React.useCallback((reason: string) => {
    clearTimer();
    if (attemptRef.current >= MAX_HANDSHAKE_RETRIES) {
      setRuntimeFailure(reason);
      setRuntimeState('unavailable');
      return;
    }
    attemptRef.current += 1;
    readyRef.current = false;
    setRuntimeState('loading');
    setLifecycle((current) => newLifecycle(current.key + 1));
  }, [clearTimer]);

  const armTimer = React.useCallback(() => {
    clearTimer();
    timeoutRef.current = window.setTimeout(() => {
      timeoutRef.current = null;
      failLifecycle('De canvas-runtime reageerde niet op tijd.');
    }, HANDSHAKE_TIMEOUT_MS);
  }, [clearTimer, failLifecycle]);

  React.useEffect(() => {
    if (!canvasOrigin) {
      setRuntimeFailure('Ongeldige editor-canvas URL.');
      setRuntimeState('unavailable');
      return;
    }
    armTimer();
    return clearTimer;
  }, [armTimer, canvasOrigin, clearTimer, lifecycle.key]);

  React.useEffect(() => {
    const onMessage = (event: MessageEvent<unknown>) => {
      if (!canvasOrigin || event.origin !== canvasOrigin) return;
      if (event.source !== iframeRef.current?.contentWindow) return;
      const { nonce, session } = lifecycleRef.current;
      const parsed = parseCanvasMessage(event.data, { nonce, session });
      if (!parsed.ok) return;
      const message = parsed.message as Record<string, any>;
      const handlers = handlersRef.current;
      const authority = authorityRef.current;
      const page = authority.pages.find((candidate) => candidate.pageId === authority.pageId);

      // Editing proposals must target what Platform is showing right now:
      // same page, same render generation, same semantic viewport.
      const currentEdit = () =>
        message.pageId === authority.pageId && message.renderId === renderIdRef.current && Boolean(page);

      switch (message.type as string) {
        case 'zoea:editor-canvas:ready':
          readyRef.current = true;
          setReadyGeneration((current) => current + 1);
          break;
        case 'zoea:editor-canvas:rendered':
          if (message.renderId !== renderIdRef.current) break;
          clearTimer();
          attemptRef.current = 0;
          setRuntimeFailure(null);
          setRuntimeState('ready');
          break;
        case 'zoea:editor-canvas:render-error':
          if (message.renderId !== renderIdRef.current) break;
          failLifecycle(typeof message.message === 'string' && message.message ? message.message : 'De canvas kon niet renderen.');
          break;
        case 'zoea:editor-canvas:height': {
          const next = Math.max(1, Math.ceil(message.height));
          canvasHeightRef.current = next;
          setCanvasHeight(next);
          break;
        }
        case 'zoea:editor-canvas:select-block':
          if (blocksOf(page).some((block) => block.blockId === message.blockId)) handlers.onSelectBlock(message.blockId);
          break;
        case 'zoea:editor-canvas:select-section':
          if (page?.sections?.some((section) => section.sectionId === message.sectionId)) handlers.onSelectSection(message.sectionId);
          break;
        case 'zoea:editor-canvas:select-chrome':
          handlers.onSelectChrome(message.chrome);
          break;
        case 'zoea:editor-canvas:navigation': {
          const href = message.href as string;
          if (!href.startsWith('/') || href.startsWith('//')) break;
          const path = href.split(/[?#]/, 1)[0] ?? '/';
          const slug = path === '/' ? '' : path.replace(/^\//, '').replace(/\/$/, '');
          const target = snapshotRef.current?.pages.find((candidate) => candidate.slug === slug);
          // The target must exist in the snapshot AND in the currently loaded pages.
          if (target && authority.pages.some((candidate) => candidate.pageId === target.pageId)) {
            handlers.onNavigatePage(target.pageId);
          }
          break;
        }
        case 'zoea:editor-canvas:edit-text':
          if (currentEdit() && blocksOf(page).some((block) => block.blockId === message.blockId)) {
            handlers.onEditText?.(message.blockId, message.text);
          }
          break;
        case 'zoea:editor-canvas:move-block': {
          if (!currentEdit() || !blocksOf(page).some((block) => block.blockId === message.blockId)) break;
          const target = message.target as { sectionId: string; columnId: string; index: number };
          const column = page?.sections
            ?.find((section) => section.sectionId === target.sectionId)
            ?.columns.find((candidate) => candidate.columnId === target.columnId);
          if (!column || target.index > column.blocks.length) break;
          handlers.onMoveBlock?.(message.blockId, target);
          break;
        }
        case 'zoea:editor-canvas:drag-block':
        case 'zoea:editor-canvas:resize-block': {
          if (!currentEdit() || message.viewport !== authority.viewport) break;
          if (!blocksOf(page).some((block) => block.blockId === message.blockId)) break;
          const allowed = message.type === 'zoea:editor-canvas:resize-block' ? RESIZE_PLACEMENT_FIELDS : DRAG_PLACEMENT_FIELDS;
          const placement = validatePlacement(message.placement, message.viewport, allowed);
          if (placement) handlers.onSetPlacement?.({ blockId: message.blockId, viewport: message.viewport, placement });
          break;
        }
      }
    };
    // Registered once per mount; lifecycle/authority are read through refs so
    // the listener is never torn down between the iframe's one-shot "ready"
    // and its arrival.
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [canvasOrigin, clearTimer, failLifecycle]);

  const post = React.useCallback((body: Record<string, unknown>) => {
    if (!canvasOrigin) return;
    const { nonce, session } = lifecycleRef.current;
    iframeRef.current?.contentWindow?.postMessage(
      { ...body, protocolVersion: EDITOR_PROTOCOL_VERSION, nonce, session },
      canvasOrigin,
    );
  }, [canvasOrigin]);

  // The first load of a lifecycle is the expected one. Any further load means
  // the iframe navigated or reloaded itself: its nonce/session/readiness are
  // stale, so tear the lifecycle down and start a fresh one.
  const loadsRef = React.useRef({ key: -1, count: 0 });
  const handleIframeLoad = React.useCallback(() => {
    const key = lifecycleRef.current.key;
    if (loadsRef.current.key !== key) loadsRef.current = { key, count: 0 };
    loadsRef.current.count += 1;
    if (loadsRef.current.count > 1) {
      clearTimer();
      readyRef.current = false;
      attemptRef.current = 0;
      renderIdRef.current += 1;
      setRuntimeState('loading');
      setLifecycle((current) => newLifecycle(current.key + 1));
      return;
    }
    if (readyRef.current) return;
    armTimer();
  }, [armTimer, clearTimer]);

  // A different canvas URL is a different runtime: never reuse the old session.
  const previousUrlRef = React.useRef(editorCanvasUrl);
  React.useEffect(() => {
    if (previousUrlRef.current === editorCanvasUrl) return;
    previousUrlRef.current = editorCanvasUrl;
    clearTimer();
    readyRef.current = false;
    attemptRef.current = 0;
    renderIdRef.current += 1;
    setRuntimeFailure(null);
    setRuntimeState('loading');
    setLifecycle((current) => newLifecycle(current.key + 1));
  }, [clearTimer, editorCanvasUrl]);

  React.useEffect(() => {
    if (readyGeneration === 0 || !snapshot || runtimeState === 'unavailable') return;
    renderIdRef.current += 1;
    const current = authorityRef.current;
    post({
      type: 'zoea:editor-canvas:render',
      renderId: renderIdRef.current,
      pageId,
      snapshot,
      assetBaseUrl: sitesApi.mediaUrl(websiteId, ''),
      viewport: { name: viewport, width: logicalWidth, height: canvasHeightRef.current },
      selection: selectionExists(current.pages.find((candidate) => candidate.pageId === pageId), current.selection) ? current.selection : NO_SELECTION,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selection has its own message; pages only gate selection validity
  }, [logicalWidth, pageId, post, readyGeneration, snapshot, viewport, websiteId]);

  const selectionKey = `${selection.kind}:${'id' in selection ? selection.id : ''}`;
  React.useEffect(() => {
    if (readyGeneration === 0 || !snapshot || runtimeState !== 'ready') return;
    const page = authorityRef.current.pages.find((candidate) => candidate.pageId === authorityRef.current.pageId);
    post({
      type: 'zoea:editor-canvas:selection',
      selection: selectionExists(page, authorityRef.current.selection) ? authorityRef.current.selection : NO_SELECTION,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on selectionKey
  }, [selectionKey, post]);

  const retryManually = () => {
    attemptRef.current = 0;
    readyRef.current = false;
    setRuntimeFailure(null);
    setRuntimeState('loading');
    setLifecycle((current) => newLifecycle(current.key + 1));
  };

  const zoomOut = () => setZoom((value) => value === 'fit' ? 80 : CANVAS_ZOOM_STEPS[Math.max(0, CANVAS_ZOOM_STEPS.indexOf(value as (typeof CANVAS_ZOOM_STEPS)[number]) - 1)]!);
  const zoomIn = () => setZoom((value) => value === 'fit' ? 100 : CANVAS_ZOOM_STEPS[Math.min(CANVAS_ZOOM_STEPS.length - 1, CANVAS_ZOOM_STEPS.indexOf(value as (typeof CANVAS_ZOOM_STEPS)[number]) + 1)]!);

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <div className="zoea-canvas-toolbar sticky top-0 z-50 mx-auto mb-2 mt-2 flex w-fit items-center gap-1 rounded-xl border border-slate-200/70 bg-white/90 px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm backdrop-blur" aria-label="Canvaswerkbalk">
        <button type="button" onClick={zoomOut} className="zoea-canvas-toolbar-button text-base leading-none" aria-label="Uitzoomen">−</button>
        <select
          aria-label="Canvas zoomniveau"
          value={zoom}
          onChange={(event) => setZoom(event.target.value === 'fit' ? 'fit' : Number(event.target.value))}
          className="zoea-canvas-zoom-select"
          data-testid="canonical-canvas-zoom"
        >
          {CANVAS_ZOOM_STEPS.map((step) => <option key={step} value={step}>{step}%</option>)}
          <option value="fit">Passend</option>
        </select>
        <button type="button" onClick={zoomIn} className="zoea-canvas-toolbar-button text-base leading-none" aria-label="Inzoomen">+</button>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <button
          type="button"
          onClick={() => setZoom('fit')}
          className={`zoea-canvas-toolbar-button${zoom === 'fit' ? ' bg-teal-50 text-teal-700' : ''}`}
        >
          Passend
        </button>
        <span className="sr-only">Beschikbare zoom: {CANVAS_ZOOM_STEPS.join(', ')} procent</span>
      </div>
      <div
        ref={surfaceRef}
        className="zoea-canvas-surface relative flex min-h-0 flex-1 overflow-auto p-6"
        data-testid="canonical-canvas-surface"
        data-runtime-state={runtimeState}
      >
      {renderError ? (
        <div className="absolute left-1/2 top-6 z-20 -translate-x-1/2 rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700 shadow-sm">
          {renderError}
        </div>
      ) : null}
      {runtimeState === 'unavailable' ? (
        <div
          role="alert"
          data-testid="canonical-runtime-unavailable"
          className="m-auto max-w-md rounded-xl border border-red-200 bg-red-50 px-6 py-5 text-sm text-red-700 shadow-sm"
        >
          <p className="font-semibold">De canonical editor-runtime is niet beschikbaar.</p>
          {runtimeFailure ? <p className="mt-1">{runtimeFailure}</p> : null}
          <button type="button" className="mt-3 rounded-lg border border-red-300 bg-white px-3 py-1.5 font-medium" onClick={retryManually}>
            Opnieuw proberen
          </button>
        </div>
      ) : (
        <div
          className="relative mx-auto shrink-0"
          style={{ width: logicalWidth * scale, height: canvasHeight * scale }}
          data-testid="canonical-canvas-frame"
        >
          {runtimeState === 'loading' ? (
            <div data-testid="canonical-runtime-loading" className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 text-sm text-slate-500">
              Canvas wordt geladen…
            </div>
          ) : null}
          <iframe
            key={lifecycle.key}
            ref={iframeRef}
            src={iframeSrc}
            title="Canonical website canvas"
            className="absolute left-0 top-0 border-0 bg-white shadow-sm"
            style={{
              width: logicalWidth,
              height: canvasHeight,
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
            }}
            referrerPolicy="no-referrer"
            onLoad={handleIframeLoad}
          />
        </div>
      )}
      </div>
    </div>
  );
}
